"use client";
// min-width to activate corresponding breakpoint
import React from "react";
import { isServer } from "@/utils/helpers";

type BreakpointType = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

const breakpoints: Record<BreakpointType, number> = {
  xs: 0,
  sm: 640,
  md: 800,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

interface UseBreakpointType {
  /**
   * list of activated breakpoints in current window's width
   */
  activatedBreakpoints: BreakpointType[];

  /**
   * current window innerWidth
   */
  width: number;

  /**
   * current window innerHeight
   */
  height: number;
}

function useBreakpoint(debounce = 500): UseBreakpointType {
  const server = isServer();
  const [breakpointData, setBreakpointData] = React.useState<UseBreakpointType>(
    {
      activatedBreakpoints: [],
      width: 0,
      height: 0,
    }
  );

  const calculateBreakpoints = (width: number, height: number) => {
    const newBps: BreakpointType[] = [];
    Object.entries(breakpoints).forEach(([key, value]) => {
      if (width >= value) {
        newBps.push(key as BreakpointType);
      }
    });
    setBreakpointData({
      activatedBreakpoints: newBps,
      width,
      height,
    });
  };

  // execute for initial render
  React.useEffect(() => {
    if (server) return;
    calculateBreakpoints(window.innerWidth, window.innerHeight);
  }, [server]);

  // execute on resizing
  React.useEffect(() => {
    if (server) return;

    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(
        () => calculateBreakpoints(window.innerWidth, window.innerHeight),
        debounce
      );
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [debounce, server]);

  return breakpointData;
}

export { useBreakpoint, breakpoints };
